import {
  useGetSaleProductsQuery,
  useSaveProductHsCodeMutation,
} from "@fero/hooks";
import type { StorageLocationProductSale } from "@fero/pages";
import { SaveSaleProduct } from "@fero-shared/api-contracts";
import { useEffect, useRef, useState } from "react";

export const useManageHsCodes = (products: StorageLocationProductSale[]) => {
  const productIds = products.map((product) => product.product.id);
  const { data: saleProductData } = useGetSaleProductsQuery(productIds);
  const saveHSCode = useSaveProductHsCodeMutation();

  const [localHSCode, setLocalHSCode] = useState<{
    [key: string]: string | null;
  }>({});
  const [hsCodeErrors, setHsCodeErrors] = useState<{ [key: string]: string }>(
    {},
  );
  const [initialHSCodeSet, setInitialHSCodeSet] = useState(false);
  const localHSCodeRef = useRef<{ [key: string]: string | null }>(localHSCode);

  useEffect(() => {
    const saleProducts = saleProductData?.body.saleProducts;
    if (!saleProducts) return;

    // Initialize local HS codes with fetched data
    const initialHSCode = saleProducts.reduce(
      (acc, product) => ({
        ...acc,
        [product.productId]: product.hsCode || "",
      }),
      {},
    );

    setLocalHSCode(initialHSCode);
    setInitialHSCodeSet(true);
    localHSCodeRef.current = initialHSCode;
  }, [saleProductData]);

  const handleHSCodeChange = (productId: string, value: string) => {
    const hsCodeValue = value.trim() === "" ? null : value.trim();
    setHsCodeErrors((prev) => ({
      ...prev,
      [productId]: "",
    }));
    setLocalHSCode((prev) => ({ ...prev, [productId]: hsCodeValue }));
  };

  const handleHSCodeBlur = async (productId: string) => {
    const hsCode = localHSCode[productId];
    const input = { productId, hsCode };
    const validationResult = SaveSaleProduct.safeParse(input);

    if (!validationResult.success) {
      const hsCodeError = validationResult.error.errors.find(
        (err) => err.path[0] === "hsCode",
      )?.message;

      setHsCodeErrors((prev) => ({
        ...prev,
        [productId]: hsCodeError || "Invalid HS Code",
      }));
      return;
    }

    await saveHSCode.mutateAsync({
      body: { productId: productId, hsCode: hsCode },
    });
  };

  return {
    localHSCode,
    hsCodeErrors,
    handleHSCodeChange,
    handleHSCodeBlur,
    localHSCodeRef,
    initialHSCodeSet,
  };
};
